import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ChevronLeft, Download, FileArchive, AlertTriangle, CheckCircle2, Truck, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { BrandLogo } from "../../components/BrandLogo";
import { adminApi, adminFileUrl, money } from "../../lib/api";
import { toast } from "sonner";

const payLabel = (s) => ({ paid: "Paid", pending: "Awaiting Payment", awaiting_zelle: "Awaiting Zelle Payment", refunded: "Refunded", failed: "Failed", expired: "Expired" }[s] || s);

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState(["Order Received", "In Production", "Ready to Ship", "Shipped"]);
  const [tracking, setTracking] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get(`/admin/orders/${id}`);
      setOrder(r.data);
      setTracking(r.data.tracking_number || "");
      const s = await adminApi.get("/admin/settings");
      if (s.data?.production_states) setStates(s.data.production_states);
    } catch (e) { if (e?.response?.status === 401) navigate("/admin/login"); else toast.error("Failed to load order."); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { if (!localStorage.getItem("mm_admin_token")) { navigate("/admin/login"); return; } load(); }, [load, navigate]);

  const setProduction = async (status) => {
    try { await adminApi.patch(`/admin/orders/${id}/production`, { production_status: status }); setOrder((o) => ({ ...o, production_status: status })); toast.success(`Production: ${status}`); if (status === "Shipped") toast.success("Shipping email sent."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const markPaid = async () => {
    try { await adminApi.post(`/admin/orders/${id}/mark-paid`); toast.success("Marked as paid."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const saveTracking = async () => {
    try { const r = await adminApi.patch(`/admin/orders/${id}/tracking`, { tracking_number: tracking }); setOrder((o) => ({ ...o, tracking_number: r.data.tracking_number, tracking_url: r.data.tracking_url })); toast.success("Tracking saved."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const refund = async () => {
    try { await adminApi.post(`/admin/orders/${id}/refund`); toast.success("Marked as refunded."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  if (loading) return <div className="flex min-h-dvh items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!order) return null;
  const paid = order.payment_status === "paid";
  const a = order.address, c = order.customer, am = order.amounts;
  const isZelle = order.payment_method === "zelle";

  return (
    <div className="min-h-dvh pb-10">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button onClick={() => navigate("/admin")} className="grid h-9 w-9 place-items-center rounded-full border bg-white"><ChevronLeft className="h-5 w-5" /></button>
          <BrandLogo size={32} />
        </div>
      </header>
      <div className="mx-auto max-w-3xl px-4 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{order.order_number}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{new Date(order.created_at).toLocaleString()}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className="rounded-full" style={{ color: paid ? "hsl(var(--success))" : "hsl(var(--warning))", borderColor: paid ? "hsl(var(--success))" : "hsl(var(--warning))" }}>{payLabel(order.payment_status)}</Badge>
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Method: {isZelle ? "Zelle" : "Card / Apple Pay"}</span>
          </div>
        </div>

        {isZelle && !paid && (
          <div className="mt-4 rounded-xl border p-4" style={{ background: "rgba(201,169,110,0.12)" }}>
            <p className="text-sm">This order is <b>Awaiting Zelle Payment</b>. Confirm the money was received before marking as paid.</p>
            <Button onClick={markPaid} data-testid="mark-paid-button" className="mt-3 h-11 rounded-xl"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Mark as Paid</Button>
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border bg-card p-4"><div className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Customer</div><p className="mt-1 text-sm">{c.name}<br />{c.email}{c.phone ? <><br />{c.phone}</> : null}</p></div>
          <div className="rounded-xl border bg-card p-4"><div className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Shipping</div><p className="mt-1 text-sm">{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}, {a.state} {a.postal_code} — {a.country}</p></div>
        </div>

        <div className="mt-3 rounded-xl border bg-card p-4">
          <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">Kit</span><span>{order.kit_qty} magnets</span></div>
          <div className="flex justify-between text-sm" data-testid="order-photo-status"><span className="text-[hsl(var(--muted-foreground))]">Photos</span><span style={order.photos_submitted ? { color: "hsl(var(--success))", fontWeight: 600 } : { color: "hsl(var(--warning))", fontWeight: 600 }}>{order.photos_submitted ? "Complete — ready for production" : `${order.photo_count_uploaded ?? 0} of ${order.photo_count_required ?? order.kit_qty} · Awaiting photos`}</span></div>
          <div className="mt-1 flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">Product subtotal</span><span>{money(am.product_subtotal_cents ?? (am.total_cents - (am.shipping_cents || 0) - (am.gift_cents || 0)))}</span></div>
          {(order.add_ons && order.add_ons.length > 0
            ? order.add_ons.map((ao) => (
                <div key={ao.id} className="flex justify-between text-sm" data-testid="order-addon"><span className="text-[hsl(var(--muted-foreground))]">{ao.title} ×{ao.qty}</span><span>{money(ao.line_total_cents)}</span></div>
              ))
            : ((order.gift_packages > 0 || am.gift_cents > 0) && (
                <div className="flex justify-between text-sm" data-testid="order-gift-packages"><span className="text-[hsl(var(--muted-foreground))]">Gift packages ×{order.gift_packages || am.gift_packages || 0}</span><span>{money(am.gift_cents || 0)}</span></div>
              )))}
          <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">Shipping</span><span>{am.shipping_cents === 0 ? "Free" : money(am.shipping_cents)}</span></div>
          <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span>{money(am.total_cents)}</span></div>
        </div>

        <div className="mt-3 rounded-xl border bg-card p-4">
          <div className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Production</div>
          {!paid ? <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Order not paid — not released for production.</p>
          : <div className="mt-2 flex flex-wrap gap-2" data-testid="production-status-controls">
              {states.map((s) => (<button key={s} onClick={() => setProduction(s)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${order.production_status === s ? "text-white" : "border bg-white"}`} style={order.production_status === s ? { background: "hsl(var(--brand-rose))" } : {}}>{s}</button>))}
            </div>}
        </div>

        <div className="mt-3 rounded-xl border bg-card p-4">
          <div className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Shipping & tracking</div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="UPS tracking number" className="h-11 flex-1 rounded-xl" data-testid="tracking-input" />
            <Button onClick={saveTracking} data-testid="save-tracking-button" className="h-11 rounded-xl"><Truck className="mr-1.5 h-4 w-4" /> Save tracking</Button>
          </div>
          {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm underline" style={{ color: "hsl(var(--brand-rose))" }}>Track package</a>}
          <p className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">Setting production to "Shipped" sends the customer a shipping email with this tracking number.</p>
        </div>

        {paid && order.payment_status === "paid" && (
          <div className="mt-3 flex justify-end">
            <Button onClick={refund} variant="ghost" data-testid="refund-button" className="h-9 rounded-lg text-xs" style={{ color: "hsl(var(--destructive))" }}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Mark as refunded</Button>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg font-semibold">Photos & files</h2>
          {paid && (
            <div className="flex gap-2">
              <a href={adminFileUrl(`/admin/orders/${id}/originals.zip`)} data-testid="download-originals-button"><Button variant="outline" className="h-10 rounded-xl border bg-white"><Download className="mr-1.5 h-4 w-4" /> All originals</Button></a>
              <a href={adminFileUrl(`/admin/orders/${id}/zip`)} data-testid="download-zip-button"><Button className="h-10 rounded-xl"><FileArchive className="mr-1.5 h-4 w-4" /> ZIP</Button></a>
            </div>
          )}
        </div>
        <div className="mt-3 space-y-3">
          {(order.magnets_summary || []).map((m) => (
            <div key={m.slot} className="flex gap-3 rounded-xl border bg-card p-3">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden bg-white shadow-[0_4px_10px_rgba(74,42,39,0.14)]" style={{ borderRadius: "22%" }}>
                {paid ? <img src={adminFileUrl(`/admin/orders/${id}/item/${m.slot}/framed`)} alt="" className="h-full w-full object-cover" />
                : <div className="grid h-full w-full place-items-center text-[10px] text-[hsl(var(--muted-foreground))]">locked</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Magnet {String(m.slot).padStart(2, "0")}</span>
                  <Badge variant="secondary" className="rounded-full text-[10px]">{m.framing_by === "customer" ? "Adjusted by customer" : "Framing by Moments Made"}</Badge>
                </div>
                <div className="truncate text-xs text-[hsl(var(--muted-foreground))]">{m.original_filename}</div>
                {m.low_res_warning && <p className="mt-1 inline-flex items-center gap-1 text-[11px]" style={{ color: "hsl(var(--warning))" }}><AlertTriangle className="h-3 w-3" /> Low resolution ({m.effective_px}px &lt; {m.min_required_px}px)</p>}
                {paid && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <a href={adminFileUrl(`/admin/orders/${id}/item/${m.slot}/original`)}><Button variant="outline" className="h-8 rounded-lg border bg-white px-2 text-xs"><Download className="mr-1 h-3 w-3" /> Original</Button></a>
                    {m.has_print && <a href={adminFileUrl(`/admin/orders/${id}/item/${m.slot}/print`)}><Button variant="outline" className="h-8 rounded-lg border bg-white px-2 text-xs"><Download className="mr-1 h-3 w-3" /> Print</Button></a>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
